import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';

import { updateCustomer } from 'api/customers';
import {
  getAllPolysomnographies,
  updatePolysomnography,
} from 'api/polysomnographies';
import { getAllTechnicians } from 'api/technicians';
import { getAllDoctors } from 'api/doctors';
import { filtersToQueryString, initialFilters } from 'helpers/filtering';
import { setDoctorOptions } from 'helpers/doctor';
import { setTechnicianOptions } from 'helpers/technician';
import { sanitizePolysomnography } from 'helpers/general';

import MainViewWithTable from 'components/main_view_with_table';

import { COLUMNS } from 'config/polysomnography';

const Polysomnographies = ({ history, onCountUpdate, onMessages }) => {
  const [meta, setMeta] = useState({});
  const [columns, setColumns] = useState(COLUMNS);
  const [fetching, setFetching] = useState(true);
  const [allPolysomnographies, setAllPolysomnographies] = useState([]);
  const [allDoctors, setDoctors] = useState([]);
  const [allTechnicians, setTechnicians] = useState([]);

  const fetchData = async (page, filters = {}, search) => {
    const filtersString = filtersToQueryString(filters, search);
    const data = await getAllPolysomnographies(page, filtersString);

    setAllPolysomnographies([...data.items]);
    setMeta(data.meta);
    setFetching(false);
  };

  useEffect(() => {
    (async () => {
      const allTechnicians = await getAllTechnicians();
      const allDoctors = await getAllDoctors();

      setDoctors(allDoctors);
      setTechnicians(allTechnicians);

      const newColumnsWithDocs = setDoctorOptions({
        columns: COLUMNS,
        data: allDoctors,
      });

      const newColumnsWithTechs = setTechnicianOptions({
        columns: newColumnsWithDocs,
        data: allTechnicians,
      });

      const initFilters = initialFilters(COLUMNS);
      await fetchData(1, initFilters);

      setFetching(false);
      setColumns(newColumnsWithTechs);
    })();
  }, []);

  const handleSelect = async (prop, value, p) => {
    const index = allPolysomnographies.findIndex((t) => t.id === p.id);

    if (prop === 'doctor') {
      value = allDoctors.find(({ id }) => Number(id) === Number(value)) || null;
    }

    if (prop === 'technician') {
      value =
        allTechnicians.find(({ id }) => Number(id) === Number(value)) || null;
    }

    const sanitizedPolysomnography = sanitizePolysomnography({ ...p });

    const {
      polysomnography,
      meta: { messages },
    } = await updatePolysomnography(p.id, {
      ...sanitizedPolysomnography,
      [prop]: value,
    });

    const newPolysomnographies = [...allPolysomnographies];
    newPolysomnographies[index] = polysomnography;

    setAllPolysomnographies([...newPolysomnographies]);
    onCountUpdate();
    onMessages(messages);
  };

  const handleDateUpdate = async (pickupDate, deliveryDate, p) => {
    const index = allPolysomnographies.findIndex((t) => t.id === p.id);

    const status =
      allPolysomnographies[index].status === 'blank'
        ? 'scheduled'
        : allPolysomnographies[index].status;

    const pickupDateStr = pickupDate?.toISOString() || null;
    const deliveryDateStr = deliveryDate?.toISOString() || null;
    const sanitizedPolysomnography = sanitizePolysomnography({ ...p });

    const {
      polysomnography,
      meta: { messages },
    } = await updatePolysomnography(p.id, {
      ...sanitizedPolysomnography,
      pickupDate: pickupDateStr,
      deliveryDate: deliveryDateStr,
      status,
    });

    const newPolysomnographies = [...allPolysomnographies];
    newPolysomnographies[index] = polysomnography;

    setAllPolysomnographies([...newPolysomnographies]);
    onCountUpdate();
    onMessages(messages);
  };

  const handleTextUpdate = async (value, p, col) => {
    const index = allPolysomnographies.findIndex((t) => t.id === p.id);
    const newPolysomnographies = [...allPolysomnographies];

    if (col.accessor === 'notes') {
      const {
        polysomnography,
        meta: { messages },
      } = await updatePolysomnography(p.id, {
        notes: value,
      });

      onMessages(messages);
      newPolysomnographies[index] = polysomnography;
    } else {
      await updateCustomer(p.customer.id, {
        [col.accessor]: value,
      });

      newPolysomnographies[index].customer[col.accessor] = value;
    }

    setAllPolysomnographies([...newPolysomnographies]);

    onCountUpdate();
  };

  const props = {
    data: allPolysomnographies,
    columns,
    fetchData,
    fetching,
    meta,
    onSelect: handleSelect,
    onTextUpdate: handleTextUpdate,
    onDatePick: handleDateUpdate,
    title: 'Polysomnographies',
    accessor: 'Polysomnographies',
  };

  return (
    <MainViewWithTable {...props}>
      <button
        className="btn-white mr5"
        data-cy="calendar"
        onClick={() => history.push('/calendar/exams?view=day')}
      >
        Calendar view
      </button>
      <button
        className="btn-green"
        data-cy="submit"
        onClick={() => history.push('/polysomnographies/add')}
      >
        Add polysomnography
      </button>
    </MainViewWithTable>
  );
};

Polysomnographies.propTypes = {
  history: ReactRouterPropTypes.history.isRequired,
  onCountUpdate: PropTypes.func.isRequired,
  onMessages: PropTypes.func.isRequired,
};

export default Polysomnographies;
